<template>
  <div
    :class="[
      'input-checkbox',
      { '-has-error': !!errorMessage },
      { '-is-disabled': disabled },
    ]">
    <div class="input-checkbox__wr">
      <input
        :id="id"
        :name="name"
        :value="value"
        type="checkbox"
        :checked="checked"
        :required="required"
        :aria-required="required"
        :disabled="disabled"
        @change="onChange" />
      <div class="input-checkbox__field" />
      <label :for="id">
        <span v-html="computedLabel" />
      </label>
    </div>

    <span
      v-show="!!errorMessage"
      class="input-checkbox__error">
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup>
  import { useField } from 'vee-validate'

  const props = defineProps({
    modelValue: [ String, Boolean ],
    id: String,
    required: {
      type: Boolean,
      default: false
    },
    checkedValue: {
      type: Boolean,
      default: undefined
    },
    value: [ String, Boolean ],
    name: String,
    label: String,
    description: String,
    disabled: Boolean,
    hidden: Boolean
  })

  const computedLabel = computed(() => {
    return `${props.required ? '*' : ''} ${props.label || props.description}`
  })

  const {
    checked, errorMessage, handleChange
  } = useField(() => props.name, undefined, {
    type: 'checkbox',
    checkedValue: props.value || true
  })

  let onChange

  if (props.checkedValue !== undefined) {
    watch(() => props.checkedValue, nval => handleChange(nval), { immediate: props.checkedValue })
  } else {
    onChange = ev => handleChange(ev)
  }
</script>

<style lang="scss" scoped>
.input-checkbox {
  --checkbox-size: 18px;
  --checkbox-content-size: 7px;

  position: relative;

  .form-loader__field & {
    label {
      >span {
        display: inline-block;

        @include body-xxs;

        line-height: 1.4;
      }
    }
  }

  &.-is-disabled {
    opacity: 0.5;
    cursor: not-allowed;

    .input-checkbox__wr {
      pointer-events: none;
    }
  }

  &.-has-error {
    .input-checkbox__field {
      border-color: color-mix(in srgb, var(--color-error) 70%, var(--color-white));

      &::before {
        background-color: color-mix(in srgb, var(--color-error) 30%, var(--color-white));
        border-color: var(--color-dark);
      }
    }
  }

  &__wr {
    position: relative;

    >label {
      position: relative;
      display: flex;
      align-items: center;
      text-align: left;
      padding-left: calc(var(--checkbox-size) + 12px);
      word-break: break-word;
      text-transform: uppercase;
      cursor: pointer;
      min-height: var(--checkbox-size);

      @include body-xxs;

      @include mq(desktop) {
        @include body-xs;
      }

      :deep(a) {
        text-decoration: underline;
      }
    }

    >input {
      position: absolute;
      opacity: 0;
    }

    .input-checkbox__wr:has(input:focus-visible) + .input-checkbox__field,
    >input:focus-visible + .input-checkbox__field,
    >input:focus,
    >input:focus + .input-checkbox__field {
      outline: 6px solid var(--outline-color, #ffe799);
      outline-offset: var(--outline-offset, -3px);
    }

    >input:indeterminate + .input-checkbox__field {
      &::after {
        height: calc(var(--checkbox-content-size) / 2);
        border-radius: 1px;
        border-color: var(--color-dark);
        opacity: 1;
      }
    }

    >input:checked + .input-checkbox__field {
      &::after {
        opacity: 1;
      }
    }

    &:hover {
      .input-checkbox__field {
        &::after {
          opacity: 0.3;
        }
      }

      >input:checked + .input-checkbox__field {
        &::after {
          background-color: var(--color-error);
        }
      }
    }
  }

  &__field {
    position: absolute;
    left: 0;
    top: 0;
    width: var(--checkbox-size);
    height: var(--checkbox-size);
    background-color: var(--color-wihte);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid rgba($color: #4a4a49, $alpha: 0.1);
    border-radius: 4px;

    &:active {
      transform: scale(0.95);
    }

    &::before,
    &::after {
      content: "";
      position: absolute;
      right: 50%;
      top: 50%;
      width: 100%;
      height: 100%;
      transform: translate(50%, -50%);
    }

    &::before {
      background-color: var(--color-white);

      &:hover {
        background-color: var(--color-black-50);
      }
    }

    &::after {
      width: var(--checkbox-content-size);
      height: var(--checkbox-content-size);
      opacity: 0;
      background-color: var(--color-red);
      border-radius: 50%;
      transition:
        opacity var(--timing-fast),
        background-color var(--timing-fast);
    }
  }

  &__error {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    color: var(--color-red);

    @include body-xs;

    // top: calc(100% + 10px);
  }
}
</style>
